import { Link } from 'react-router-dom'
import maharajPortrait from '../../../assets/images/maharaj-ji.png'
import { useReviewsAggregate } from '../../../hooks/useReviews'
import { StarRatingDisplay } from '../reviews/StarRating'
import { startingPrice } from '../booking/maharajPricingData'
import { useMaharajTiers } from '../booking/useMaharajTiers'

export default function MaharajProfileHeroSection({ astro }) {
  const tiers = useMaharajTiers()
  const { summary } = useReviewsAggregate('astrologer', astro?.id)
  const liveRating = summary.averageRating
  const liveReviewCount = summary.totalReviews
  const ratingLabel = liveReviewCount > 0 ? liveRating.toFixed(1) : '4.9'
  const ratingValue = liveReviewCount > 0 ? liveRating : 4.9

  return (
    <div className="relative overflow-hidden bg-primary text-white pt-28 pb-16">
      <div aria-hidden="true" className="absolute inset-0 bg-linear-to-b from-[#3a0a14] via-primary to-[#2a0710]" />
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(55%_70%_at_50%_0%,rgba(232,119,15,0.20),transparent_70%)]" />
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(45%_55%_at_100%_100%,rgba(232,119,15,0.10),transparent_70%)]" />
      <div
        aria-hidden="true"
        className="absolute -right-24 -top-16 w-104 h-104 opacity-[0.08] animate-[spin_150s_linear_infinite] motion-reduce:animate-none flex items-center justify-center pointer-events-none"
      >
        <span className="text-[190px] text-[#E6C878] select-none">ॐ</span>
      </div>

      <div className="max-w-350 mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/40 mb-8">
          <Link to="/" className="hover:text-secondary transition-colors">Home</Link>
          <span className="opacity-40">/</span>
          <span className="text-white/70 normal-case font-semibold">Raju Maharaj Ji</span>
        </div>

        <div className="grid gap-10 grid-cols-1 lg:grid-cols-12 items-center">
          {/* Portrait */}
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-[28px] border-2 border-secondary/40 shadow-2xl min-h-95 lg:min-h-115">
              <span className="corner-flourish tl" aria-hidden="true" style={{ borderColor: 'rgba(230,200,120,.55)' }} />
              <span className="corner-flourish tr" aria-hidden="true" style={{ borderColor: 'rgba(230,200,120,.55)' }} />
              <span className="corner-flourish bl" aria-hidden="true" style={{ borderColor: 'rgba(230,200,120,.55)' }} />
              <span className="corner-flourish br" aria-hidden="true" style={{ borderColor: 'rgba(230,200,120,.55)' }} />
              <img
                src={maharajPortrait}
                alt="Raju Maharaj Ji — Vedic Jyotish Acharya"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top,rgba(11,8,6,.55) 0%,transparent 45%)' }}
              />
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[9px] font-bold uppercase tracking-[.2em] text-[#1C140A]"
                  style={{ background: 'linear-gradient(135deg,#D4AF37 0%,#F7D08F 100%)' }}
                >
                  ✦ VIP Access
                </span>
              </div>
              <div className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-white shadow">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Available now
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="space-y-6 lg:col-span-7">
            <div className="flex flex-wrap items-center gap-2">
              <div className="hero-chip inline-flex">
                <span className="text-secondary">✦</span> Vedic Jyotish Acharya · Founder
              </div>
              <div className="hero-card-badge">
                <svg className="w-3 h-3 text-secondary" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 16 3 6l5.5 4L12 4l3.5 6L21 6l-2 10H5Zm0 2h14v2H5v-2Z" />
                </svg>
                By Invitation
              </div>
            </div>

            <div>
              <h1 className="text-4xl sm:text-5xl font-cinzel leading-tight">
                Raju <span className="text-gradient-gold">Maharaj Ji</span>
              </h1>
              <p className="mt-2 text-sm text-white/50 uppercase tracking-[0.3em]">Jyotish · Palmistry · Vastu Shastra Master</p>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="flex items-center gap-1.5">
                <StarRatingDisplay value={ratingValue} size="md" />
                <span className="font-semibold text-white">{ratingLabel}</span>
                <span className="text-white/40">
                  ({liveReviewCount > 0 ? `${liveReviewCount} review${liveReviewCount === 1 ? '' : 's'}` : '4,800+ clients'})
                </span>
              </span>
              <span className="w-px h-4 bg-white/15" />
              <span>Hindi, English, Sanskrit</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="hero-stat-block text-center">
                <h4 className="text-xl font-semibold">4,800+</h4>
                <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#8B7355]">Verified clients</p>
              </div>
              <div className="hero-stat-block text-center">
                <h4 className="text-xl font-semibold">98%</h4>
                <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#8B7355]">Success rate</p>
              </div>
              <div className="hero-stat-block text-center">
                <h4 className="text-xl font-semibold">15+</h4>
                <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#8B7355]">Years mastery</p>
              </div>
              <div className="hero-stat-block text-center">
                <h4 className="text-xl font-semibold">10K+</h4>
                <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#8B7355]">Sessions held</p>
              </div>
            </div>

            <div className="astro-divider" />

            <div className="flex flex-wrap items-center justify-between gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Priority audience from</p>
                <p className="mt-1 text-2xl font-bold text-secondary">
                  ₹{startingPrice(tiers).toLocaleString('en-IN')} <span className="text-sm font-medium text-white/40">/ audience</span>
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919091840899" className="hero-btn-secondary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.2em]">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25V16.14a2.25 2.25 0 0 0-1.545-2.14l-4.22-1.354a2.25 2.25 0 0 0-2.586 1.022l-1.411 2.334a15.682 15.682 0 0 1-6.75-6.75l2.334-1.411a2.25 2.25 0 0 0 1.022-2.586l-1.354-4.22A2.25 2.25 0 0 0 6.14 2.25H4.5a2.25 2.25 0 0 0-2.25 2.25v2.25Z" />
                  </svg>
                  Call
                </a>
                <Link to="/book-maharaj-ji" className="hero-btn-primary premium-shine inline-flex items-center justify-center rounded-full px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] whitespace-nowrap">
                  Book Audience
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
